import Pusher from 'pusher-js';
import xhr from 'xhr.js';
import config from 'config';

// We use pusher-js directly rather than laravel-echo because the API broadcasts
// via `broadcastAs('export.ready')`, which strips the App\Events\... namespace
// Echo would otherwise unwrap. With a flat event name, Echo adds no value over
// `channel.bind('export.ready', handler)` — and skipping it keeps the bundle
// smaller and avoids a second abstraction layer over Pusher.
let pusherInstance = null;

/**
 * Initialize the singleton Pusher client. Safe to call multiple times —
 * subsequent calls return the existing instance. Returns null when PUSHER_KEY
 * is unset (e.g., dev without creds), so call sites can no-op.
 *
 * Channel subscriptions are owned by ExportNotificationsProvider and
 * StudyNotificationsProvider, not this module. Both share the same singleton.
 * teardownEcho() is called on logout or user changes to disconnect and discard
 * the instance, ensuring the next login establishes a fresh connection
 * authenticated for the new user.
 *
 * @returns {Pusher|null} Singleton Pusher instance, or null when no key is configured.
 */
export function initializeEcho() {
    if (pusherInstance) {
        return pusherInstance;
    }
    if (!config.PUSHER_KEY) {
        return null;
    }

    const pusherOptions = {
        cluster: config.PUSHER_CLUSTER,
        forceTLS: config.PUSHER_FORCE_TLS !== false,
        // Why this `authorizer` exists at all:
        // The API broadcasts `export.ready` on a *private* channel scoped to
        // the requesting user (`private-App.Models.User.{id}`) so a user only
        // ever sees their own exports — broadcasting on a public channel would
        // leak other users' export filenames and ids to anyone subscribed.
        // Private channels require pusher-js to call `/broadcasting/auth` to
        // prove the subscriber owns the channel; Laravel's BroadcastController
        // checks the session and signs the subscription.
        //
        // We must override the default pusher-js authorizer because it issues
        // a bare XHR without `withCredentials`, which drops our session cookie
        // and fails auth. Routing through our `xhr.js` instance forwards the
        // cookie and reuses the same baseURL/CSRF setup as the rest of the app.
        authorizer: channel => ({
            authorize: (socketId, callback) => {
                xhr.post(
                    '/broadcasting/auth',
                    { socket_id: socketId, channel_name: channel.name },
                    { withCredentials: true }
                )
                    .then(response => callback(null, response.data))
                    .catch(error => callback(error));
            }
        })
    };

    // Local soketi override. When PUSHER_HOST is set, pusher-js skips the
    // cluster-derived ws-{cluster}.pusher.com endpoint and connects directly.
    if (config.PUSHER_HOST) {
        pusherOptions.wsHost = config.PUSHER_HOST;
        pusherOptions.wsPort = config.PUSHER_PORT;
        pusherOptions.wssPort = config.PUSHER_PORT;
        pusherOptions.enabledTransports = ['ws', 'wss'];
        pusherOptions.disableStats = true;
    }

    pusherInstance = new Pusher(config.PUSHER_KEY, pusherOptions);

    // Legacy bridge: StudyPage subscribes to `panelfox-ws` via `window.pusher`.
    // Keep it mirrored here until that surface migrates to `getEcho()` so its
    // study-viewers / sp-update / lock-update events keep flowing.
    window.pusher = pusherInstance;

    return pusherInstance;
}

/**
 * Disconnect and discard the singleton Pusher client. Called on logout so the
 * next login establishes a fresh connection authenticated for the new user.
 *
 * @returns {void}
 */
export function teardownEcho() {
    if (pusherInstance) {
        pusherInstance.disconnect();
        pusherInstance = null;
        window.pusher = null;
    }
}

/**
 * Read-only accessor for the current singleton, mainly for diagnostics.
 *
 * @returns {Pusher|null}
 */
export function getEcho() {
    return pusherInstance;
}

/**
 * Returns the private Pusher channel name for a given user ID.
 * Follows Laravel's broadcasting convention for private user channels.
 *
 * @param {number|string} userId - The authenticated user's ID
 * @returns {string} The private channel name (e.g., 'private-App.User.123')
 */
export function getPrivateUserChannel(userId) {
    return `private-App.User.${userId}`;
}
